import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, AlertCircle, Eye, EyeOff, MessageSquare, ShieldCheck, Zap } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ForgotPasswordModal } from '../components/auth/ForgotPasswordModal';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { verifyPassword, checkRateLimit, recordAttempt, sanitizeEmail } from '../utils/security';
import { SEO } from '../components/SEO';
export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const {
    login
  } = useAuth();
  const {
    language,
    t
  } = useLanguage();
  const navigate = useNavigate();
  const isRTL = language === 'ar';
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    const cleanEmail = sanitizeEmail(email);
    // Check rate limit before attempting login
    const rateCheck = checkRateLimit(`login_${cleanEmail}`);
    if (!rateCheck.allowed) {
      const msg = isRTL ? `تم تجاوز عدد المحاولات المسموحة. يرجى الانتظار ${rateCheck.lockoutMinutes} دقيقة.` : `Too many login attempts. Please wait ${rateCheck.lockoutMinutes} minutes.`;
      setError(msg);
      setIsLoading(false);
      return;
    }
    try {
      // Use the auth login function which checks Supabase first, then localStorage
      await login(cleanEmail, password);
      // Success - clear rate limit
      recordAttempt(`login_${cleanEmail}`, true);
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Login error:', error);
      recordAttempt(`login_${cleanEmail}`, false);
      const remaining = checkRateLimit(`login_${cleanEmail}`).remainingAttempts;
      // Determine error message
      let errorMsg = error.message || t('auth.loginFailed');
      // If it's a wrong password error, show remaining attempts
      if (error.message?.includes('كلمة المرور غير صحيحة') || error.message?.includes('Wrong password')) {
        errorMsg = remaining > 0 ? isRTL ? `كلمة المرور غير صحيحة. متبقي ${remaining} محاولات.` : `Wrong password. ${remaining} attempts remaining.` : t('auth.wrongPassword');
      }
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-white flex" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-2360">
      <SEO title={isRTL ? 'تسجيل الدخول' : 'Login'} description={isRTL ? 'سجّل دخولك إلى Operines وابدأ إدارة بوت واتساب الذكي الخاص بك.' : 'Log in to Operines and start managing your AI WhatsApp bot.'} canonicalPath="/login" language={language as 'ar' | 'en'} noIndex={true} data-id="element-2361" />

      {/* LEFT SIDE - FORM */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 xl:px-24 bg-white relative z-10" data-id="element-2362">
        <div className="w-full max-w-md mx-auto lg:mx-0 lg:max-w-lg py-8 lg:py-12" data-id="element-2363">
          {/* Logo */}
          <Link to="/" className="inline-block mb-8 lg:mb-12" data-id="element-2364">
            <img src="https://cdn.magicpatterns.com/uploads/8sRwXksp5brxBQY4QcgZ5M/U062aU0635U0645U064aU0645_U0628U062fU0648U0646_U0639U0646U0648U0627U0646_(37).png" alt="Operines" className="h-8 sm:h-10 w-auto" data-id="element-2365" />
          </Link>

          {/* Heading */}
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900 mb-4" data-id="element-2366">
            {t('auth.login')}
          </h2>

          <div className="mb-8" data-id="element-2367">
            <p className="text-base text-gray-600" data-id="element-2368">
              {t('common.or')}{' '}
              <Link to="/register" className="font-semibold text-[#4f1964] hover:text-[#3d1350] transition-colors underline decoration-2 decoration-transparent hover:decoration-[#4f1964]" data-id="element-2369">
                {t('auth.createAccount')}
              </Link>
            </p>
          </div>

          {/* Form Container */}
          <div className="bg-white shadow-xl lg:shadow-none rounded-2xl lg:rounded-none p-6 lg:p-0 border lg:border-0 border-gray-100" data-id="element-2370">
            {error && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3" data-id="element-2371">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-2372" />
                <div className="flex-1" data-id="element-2373">
                  <p className="text-sm text-red-800 font-medium" data-id="element-2374">{error}</p>
                  {error.includes(t('auth.emailNotRegistered').split('.')[0]) && <p className="text-xs text-red-600 mt-1" data-id="element-2375">
                      <Link to="/register" className="underline hover:no-underline font-medium" data-id="element-2376">
                        {t('auth.clickHereToRegister')}
                      </Link>
                    </p>}
                </div>
              </motion.div>}

            <form className="space-y-5" onSubmit={handleSubmit} data-id="element-2377">
              <Input label={t('auth.email')} type="email" value={email} onChange={e => setEmail(e.target.value)} required isRTL={isRTL} data-id="element-2378" />

              <div className="relative" data-id="element-2379">
                <Input label={t('auth.password')} type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required isRTL={isRTL} data-id="element-2380" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-9 text-gray-400 hover:text-gray-600 transition-colors`} data-id="element-2381">
                  {showPassword ? <EyeOff className="w-5 h-5" data-id="element-2382" /> : <Eye className="w-5 h-5" data-id="element-2383" />}
                </button>
              </div>

              <div className="flex items-center justify-between pt-1" data-id="element-2384">
                <div className="flex items-center" data-id="element-2385">
                  <input id="remember-me" name="remember-me" type="checkbox" className="h-4 w-4 text-[#4f1964] focus:ring-[#4f1964] border-gray-300 rounded cursor-pointer" data-id="element-2386" />
                  <label htmlFor="remember-me" className={`${isRTL ? 'mr-2' : 'ml-2'} block text-sm text-gray-700 cursor-pointer select-none`} data-id="element-2387">
                    {t('auth.rememberMe')}
                  </label>
                </div>

                <div className="text-sm" data-id="element-2388">
                  <button type="button" onClick={() => setShowForgotPassword(true)} className="font-medium text-[#4f1964] hover:text-[#3d1350] transition-colors" data-id="element-2389">
                    {t('auth.forgotPassword')}
                  </button>
                </div>
              </div>

              <div className="pt-2" data-id="element-2390">
                <Button type="submit" fullWidth isLoading={isLoading} size="lg" className="bg-[#4f1964] hover:bg-[#3d1350] text-white shadow-lg shadow-purple-200 py-3 sm:py-4 text-base" data-id="element-2391">
                  {t('auth.loginButton')}
                  {isRTL ? <ArrowLeft className="mr-2 w-5 h-5" data-id="element-2392" /> : <ArrowRight className="ml-2 w-5 h-5" data-id="element-2393" />}
                </Button>
              </div>
            </form>

            <div className="mt-8" data-id="element-2394">
              <div className="relative" data-id="element-2395">
                <div className="absolute inset-0 flex items-center" data-id="element-2396">
                  <div className="w-full border-t border-gray-200" data-id="element-2397" />
                </div>
                <div className="relative flex justify-center text-sm" data-id="element-2398">
                  <span className="px-4 bg-white text-gray-500 font-medium" data-id="element-2399">
                    {t('auth.noAccount')}
                  </span>
                </div>
              </div>

              <div className="mt-6" data-id="element-2400">
                <Link to="/register" data-id="element-2401">
                  <Button variant="outline" fullWidth className="py-3 text-gray-700 border-gray-300 hover:bg-gray-50" data-id="element-2402">
                    {t('auth.createAccount')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - DECORATIVE (Hidden on mobile) */}
      <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-[#4f1964] to-purple-800 overflow-hidden items-center justify-center p-12" data-id="element-2403">
        {/* Abstract Background Shapes */}
        <div className="absolute top-0 right-0 w-full h-full overflow-hidden" data-id="element-2404">
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 120,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute -bottom-[30%] -left-[20%] w-[100%] h-[100%] border border-white/5 rounded-full" data-id="element-2405" />
        </div>

        {/* Content Container */}
        <div className="relative z-10 max-w-lg w-full text-center text-white" data-id="element-2406">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.2,
          duration: 0.6
        }} className="mb-12" data-id="element-2407">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl border border-white/20" data-id="element-2408">
              <MessageSquare className="w-10 h-10 text-white" data-id="element-2409" />
            </div>
            <h2 className="text-3xl font-bold mb-4" data-id="element-2410">
              {isRTL ? 'مرحباً بك مجدداً' : 'Welcome Back'}
            </h2>
            <p className="text-lg text-white/80 max-w-md mx-auto" data-id="element-2411">
              {isRTL ? 'استكمل رحلة الأتمتة الخاصة بك وتابع أداء البوت الذكي.' : 'Continue your automation journey and monitor your AI bot performance.'}
            </p>
          </motion.div>

          {/* Feature Highlights */}
          <div className="grid grid-cols-2 gap-4" data-id="element-2412">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-6 text-left" data-id="element-2413">
              <ShieldCheck className="w-8 h-8 text-green-300 mb-3" data-id="element-2414" />
              <h4 className="font-bold mb-1" data-id="element-2415">
                {isRTL ? 'آمن وموثوق' : 'Secure & Reliable'}
              </h4>
              <p className="text-xs text-white/60" data-id="element-2416">
                {isRTL ? 'تشفير كامل للبيانات' : 'End-to-end encryption'}
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-6 text-left" data-id="element-2417">
              <Zap className="w-8 h-8 text-yellow-300 mb-3" data-id="element-2418" />
              <h4 className="font-bold mb-1" data-id="element-2419">
                {isRTL ? 'سريع جداً' : 'Lightning Fast'}
              </h4>
              <p className="text-xs text-white/60" data-id="element-2420">
                {isRTL ? 'ردود فورية 24/7' : 'Instant 24/7 replies'}
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      <ForgotPasswordModal isOpen={showForgotPassword} onClose={() => setShowForgotPassword(false)} data-id="element-2421" />
    </div>;
}